import { AddRightsStatusEnum } from "@entities/addRights/lib/constants";
import AddMediaLinkForm from "@entities/addRights/ui/AddMediaLinkForm";
import { IAddRights } from "@shared/lib/types/addRightsType";
import { Button, DatePicker } from "@shared/ui";
import { Close } from "@shared/ui/icons";
import { Collapse, Flex, Tooltip } from "antd";
import dayjs from "dayjs";
import { AlertTriangle, Calendar, Check, Edit, ExternalLink, Info, Shield } from "lucide-react";
import { useState } from "react";
import styles from "../styles.module.scss";
import { getAddRightsStatusDisplay } from "../utils";

interface AdRightsCardProps {
  addRights: IAddRights[];
  deliverables?: { key: string; label: string }[];
  expandedAddRightKey: string | null;
  onExpandedAddRightKeyChange: (key: string | null) => void;
  editingStartDateAddRightId: string | null;
  onEditingStartDateAddRightIdChange: (id: string | null) => void;
  tempStartDate: string | null;
  onTempStartDateChange: (date: string | null) => void;
  onStartAdRight: (params: {
    adRightsTrackerId: string;
    startDate: string;
  }) => void;
  isStarting: boolean;
  refetch?: () => void;
}

export function AdRightsCard({
  addRights,
  deliverables = [],
  expandedAddRightKey,
  onExpandedAddRightKeyChange,
  editingStartDateAddRightId,
  onEditingStartDateAddRightIdChange,
  tempStartDate,
  onTempStartDateChange,
  onStartAdRight,
  isStarting,
  refetch
}: AdRightsCardProps) {
  const [editingMediaId, setEditingMediaId] = useState<string | null>(null);

  return (
    <div className={styles.adRightsCard}>
      <div className={styles.adRightsCardHeader}>
        <h4 className={styles.adRightsCardTitle}>
          <Shield size={14} className={styles.adRightsCardTitleIcon} /> Ad
          Rights & Usage
        </h4>
        <span className={styles.adRightsCardCount}>
          {addRights.length} Ad Rights
        </span>
      </div>
      <div className={styles.adRightsCardList}>
        {addRights.length === 0 ? (
          <div className={styles.adRightsEmptyState} style={{ padding: "24px 16px", textAlign: "center" }}>
            <div className={styles.adRightsEmptyIcon} style={{ marginBottom: 8, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <Shield size={18} />
            </div>
            <p className={styles.adRightsEmptyText}>
              No ad rights are configured for this campaign.
            </p>
          </div>
        ) : (
          <Collapse
            accordion
            activeKey={expandedAddRightKey ?? undefined}
            onChange={(keys) => {
              const key = Array.isArray(keys) ? keys[0] : keys;
              onExpandedAddRightKeyChange(key ?? null);
            }}
            className={styles.adRightsCollapse}
            expandIconPosition="end"
            items={addRights.map((item) => {
              const display = getAddRightsStatusDisplay(item.status);
              const itemKey = item._id;
              const isEditingDate = editingStartDateAddRightId === item._id;

              const isCancelledOrWithdrawn =
                item.status === AddRightsStatusEnum.Cancelled ||
                item.status === AddRightsStatusEnum.Withdrawn;

              const linkedDeliverable = deliverables.find(
                (d) =>
                  d.key === item.campaignDeliverableId ||
                  d.key === item.briefDeliverableId
              );
              const formatLabel = linkedDeliverable?.label;

              return {
                key: itemKey,
                label: (
                  <div id={`ad-right-${item._id}`} className={styles.adRightsRowHeaderLabel}>
                    <div className={styles.adRightsRowLeft}>
                      <div className={styles.adRightsRowTitle}>
                        {formatLabel || "Others"}
                      </div>
                      <div className={styles.adRightsRowMeta}>
                        {item.adRightsType || "—"}{item.adRightsDurationInDays ? ` • ${item.adRightsDurationInDays} Days` : ""}
                      </div>
                    </div>
                      <span
                        className={`${styles.adRightsStatusTag} ${styles[display.classNameKey]}`}
                      >
                        {display.label}
                      </span>
                  </div>
                ),
                children: (
                  <div className={styles.adRightsRowContentInner}>
                    <div className={styles.adRightsTypeBlock}>
                      <span className={styles.adRightsLabel}>
                        Ad Rights Type
                      </span>
                      <div className={styles.adRightsTypeValue}>
                        {item.adRightsType}
                      </div>
                    </div>
                    {item.description && (
                      <div style={{ marginTop: 12 }}>
                        <span className={styles.adRightsLabel}>
                          Description
                        </span>
                        <div className={styles.adRightsDesc} style={{ marginTop: 4 }}>
                          {item.description}
                        </div>
                      </div>
                    )}
                    {([
                        AddRightsStatusEnum.Active,
                        AddRightsStatusEnum.Expired,
                        AddRightsStatusEnum.Upcoming,
                        AddRightsStatusEnum.Cancelled,
                        AddRightsStatusEnum.Withdrawn,
                      ] as string[]).includes(item.status) && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
                        <Flex gap={16} wrap="wrap">
                            <div>
                              <span className={styles.adRightsLabel}>Start Date</span>
                              <div className={styles.adRightsDateValue}>
                                <Calendar size={12} />
                                {item.startDate && dayjs(item.startDate).isValid()
                                  ? dayjs(item.startDate).format("DD MMM YYYY")
                                  : "—"}
                              </div>
                            </div>
                            <div>
                              <span className={styles.adRightsLabel}>End Date</span>
                              <div className={styles.adRightsDateValue}>
                                <Calendar size={12} />
                                {item.endDate && dayjs(item.endDate).isValid()
                                  ? dayjs(item.endDate).format("DD MMM YYYY")
                                  : "—"}
                              </div>
                            </div>
                          </Flex>
                        {/* Media Links — read-only display */}
                        {((item.mediaLink && item.mediaLink.trim() !== "") || (item.downloadableMediaUrls && item.downloadableMediaUrls.length > 0)) && (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {item.mediaLink && (
                              <div>
                                <span className={styles.adRightsLabel} style={{ marginBottom: 0 }}>Media Link</span>
                                <div className={styles.adRightsDateValue} style={{ marginTop: 4 }}>
                                  <ExternalLink size={12} />
                                  <a
                                    href={item.mediaLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#635bff", textDecoration: "underline", fontSize: 12 }}
                                  >
                                    {item.mediaLink}
                                  </a>
                                  {item.isMediaLinkNonTrackable && (
                                    <Tooltip title={item.nonTrackableReason || "Non-Trackable Link"}>
                                      <AlertTriangle size={12} color="#dc2626" style={{ cursor: "help" }} />
                                    </Tooltip>
                                  )}
                                </div>
                              </div>
                            )}
                            {item.downloadableMediaUrls && item.downloadableMediaUrls.length > 0 && (
                              <div>
                                <span className={styles.adRightsLabel} style={{ marginBottom: 0 }}>Uploaded Files</span>
                                <Flex vertical gap={4} style={{ marginTop: 4 }}>
                                  {item.downloadableMediaUrls.map((url, idx) => (
                                    <div key={idx} className={styles.adRightsDateValue}>
                                      <ExternalLink size={12} />
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ color: "#635bff", textDecoration: "underline", fontSize: 12 }}
                                      >
                                        Asset {idx + 1}
                                      </a>
                                    </div>
                                  ))}
                                </Flex>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {(item.status === AddRightsStatusEnum.Pending || item.status === AddRightsStatusEnum.ActionPending) && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
                        {(((item.mediaLink && item.mediaLink.trim() !== "") || (item.downloadableMediaUrls && item.downloadableMediaUrls.length > 0)) && editingMediaId !== item._id) ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {item.mediaLink && (
                              <div>
                                  <span className={styles.adRightsLabel} style={{ marginBottom: 0 }}>Media Link</span>  
                                <div className={styles.adRightsDateValue} style={{ marginTop: 4 }}>
                                  <ExternalLink size={12} />
                                  <a
                                    href={item.mediaLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#635bff", textDecoration: "underline", fontSize: 12 }}
                                  >
                                    {item.mediaLink}
                                  </a>
                                  {item.isMediaLinkNonTrackable && (
                                    <Flex align="center" gap={4} style={{ display: "inline-flex", verticalAlign: "middle" }}>
                                      <Tooltip title={item.nonTrackableReason || "Non-Trackable Link"}>
                                        <AlertTriangle size={12} color="#dc2626" style={{ cursor: "help" }} />
                                      </Tooltip>
                                      <Tooltip title="Edit Link">
                                        <Button
                                          type="text"
                                          size="small"
                                          icon={<Edit size={12} />}
                                          onClick={() => setEditingMediaId(item._id)}
                                          style={{ padding: 0, height: "max-content", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                                        />
                                      </Tooltip>
                                    </Flex>
                                  )}
                                </div>
                              </div>
                            )}
                            {item.downloadableMediaUrls && item.downloadableMediaUrls.length > 0 && (
                              <div>
                                  <span className={styles.adRightsLabel} style={{ marginBottom: 0 }}>Uploaded Files</span>                                
                                <Flex vertical gap={4} style={{ marginTop: 4 }}>
                                  {item.downloadableMediaUrls.map((url, idx) => (
                                    <div key={idx} className={styles.adRightsDateValue}>
                                      <ExternalLink size={12} />
                                      <a
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ color: "#635bff", textDecoration: "underline", fontSize: 12 }}
                                      >
                                        Asset {idx + 1}
                                      </a>
                                    </div>
                                  ))}
                                </Flex>
                              </div>
                            )}
                          </div>
                        ) : (
                          (!item.isMediaLinkNonTrackable || editingMediaId === item._id) && (
                            <AddMediaLinkForm
                              adRightsTrackerId={item._id}
                              initialMediaLink={item.mediaLink}
                              onSuccess={() => {
                                refetch?.();
                                setEditingMediaId(null);
                              }}
                              onCancel={editingMediaId === item._id ? () => setEditingMediaId(null) : undefined}
                            />
                          )
                        )}
                        {item.status === AddRightsStatusEnum.ActionPending && (
                          <div
                            className={styles.adRightsActionRequired}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className={styles.adRightsActionRequiredText}>
                              <Info size={14} />
                              Start date selection required.
                            </div>
                            {isEditingDate ? (
                              <Flex align="center" gap={4}>
                                <DatePicker
                                  size="small"
                                  value={
                                    tempStartDate ? dayjs(tempStartDate) : null
                                  }
                                  onChange={(_, dateString) => {
                                    onTempStartDateChange(
                                      dateString ? String(dateString) : null
                                    );
                                  }}
                                  format="DD MMM YYYY"
                                  style={{
                                    width: 140,
                                    height: "25px",
                                  }}
                                />
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<Check size={14} />}
                                  style={{ height: "25px" }}
                                  onClick={() => {
                                    if (tempStartDate) {
                                      onStartAdRight({
                                        adRightsTrackerId: item._id,
                                        startDate:
                                          dayjs(tempStartDate).toISOString(),
                                      });
                                      onEditingStartDateAddRightIdChange(null);
                                      onTempStartDateChange(null);
                                    }
                                  }}
                                  disabled={isStarting || !tempStartDate}
                                  className={styles.adRightsDateSuffixBtn}
                                />
                                <Button
                                  type="text"
                                  size="small"
                                  style={{ height: "25px" }}
                                  icon={<Close size={14} />}
                                  onClick={() => {
                                    onEditingStartDateAddRightIdChange(null);
                                    onTempStartDateChange(null);
                                  }}
                                  disabled={isStarting}
                                  className={styles.adRightsDateSuffixBtn}
                                />
                              </Flex>
                            ) : (
                              <Button
                                size="small"
                                onClick={() => {
                                  onEditingStartDateAddRightIdChange(item._id);
                                  onTempStartDateChange(null);
                                }}
                                loading={isStarting}
                                disabled={isStarting}
                                style={{
                                  padding: "4px 10px",
                                  fontSize: 10,
                                  height: "max-content",
                                }}
                              >
                                Select Date
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ),
              };
            })}
          />
        )}
      </div>
    </div>
  );
}
